<script setup lang="ts">
import type { ICustomerSearchResource } from '@/types/core/Customer'
import { searchResources } from '@/services/core.service'
import { ElMessage } from 'element-plus'
import { ref } from 'vue'
import { cloneDeep } from 'lodash'

const ownedByCompanyOwnerOption = { value: { id: 0, name: 'Owned by us' }, label: 'Owned by us' }

export interface ICustomerIdentifier {
  id: number
  name: string
}

// Only to display lookup message
const initial = ref(true)

// Allow to receive the name along with id, for the initial render
const selectedCustomer = defineModel<ICustomerIdentifier>()

const customerFilterOptions = ref<{ label: string; value: ICustomerIdentifier }[]>([])

const customerFilterLoading = ref(false)

// Initial options... Owned by us plus the one already set
customerFilterOptions.value.push(ownedByCompanyOwnerOption)
if (selectedCustomer.value?.id && selectedCustomer.value.name) {
  customerFilterOptions.value.push({
    label: selectedCustomer.value.name,
    value: cloneDeep(selectedCustomer.value),
  })
}

/**
 * Remote method for customer filter.
 *
 * @param query customer query fragment.
 */
const filterCustomers = async (query: string) => {
  if (query == '' || query.length < 3) return

  initial.value = false

  try {
    customerFilterLoading.value = true
    const filteredCustomers = await searchResources({
      resource: 'customer',
      fragment: query,
    })
    customerFilterOptions.value = filteredCustomers.map((customer: ICustomerSearchResource) => {
      return {
        label: customer.name,
        value: customer,
      }
    })
    // Add "Owned by Company" option at the end
    customerFilterOptions.value.push(ownedByCompanyOwnerOption)
  } catch {
    ElMessage.error('Failed to filter customers')
    customerFilterOptions.value = []
  } finally {
    customerFilterLoading.value = false
  }
}
</script>

<template>
  <el-select-v2
    v-model="selectedCustomer"
    :options="customerFilterOptions"
    :loading="customerFilterLoading"
    remote
    :remote-method="filterCustomers"
    filterable
    clearable
    value-key="id"
    placeholder="Select Owner"
    loading-text="Loading customers..."
  >
    <template v-if="initial" #footer>
      <el-text> Start typing to lookup... </el-text>
    </template>
  </el-select-v2>
</template>

<style scoped lang="scss">
.el-text {
  color: var(--text-placeholder);
}
</style>
